/**
 * Community category page — /community/c/:slug
 *
 * Shows the list of threads in one category with sort + pagination.
 * "New thread" composer surfaces when the user can post in this
 * category (the API does the gate; we don't reveal a composer the
 * server will reject).
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  createThread,
  fetchThreads,
  type ThreadListItem,
  type ThreadListResponse,
  type ThreadSort,
} from "@/lib/community-api";
import type { MarkdownComposerProps } from "@/components/MarkdownComposer";
import { RichTextEditor } from "@/components/RichTextEditor";

/**
 * The composer component to render — Tiptap by default; tests inject
 * MarkdownComposer because JSDOM's incomplete contenteditable support
 * doesn't let userEvent.type reach Tiptap's prosemirror surface.
 */
type ComposerComponent = (props: MarkdownComposerProps) => React.JSX.Element;

const PAGE_SIZE = 20;

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: ThreadListResponse }
  | { kind: "not_member" }
  | { kind: "category_unknown" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function formatActivityTime(iso: string): string {
  const date = new Date(iso);
  const now = Date.now();
  const ms = now - date.getTime();
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

export interface CommunityCategoryPageProps {
  /** Test seam — inject a mocked fetcher. */
  fetchThreadsFn?: typeof fetchThreads;
  /** Test seam — inject a mocked create. */
  createThreadFn?: typeof createThread;
  onUnauthenticated?: (returnTo: string) => void;
  /** Test seam — swap the composer (default RichTextEditor; tests pass MarkdownComposer). */
  Composer?: ComposerComponent;
}

export function CommunityCategoryPage({
  fetchThreadsFn = fetchThreads,
  createThreadFn = createThread,
  onUnauthenticated = redirectToLogin,
  Composer = RichTextEditor,
}: CommunityCategoryPageProps = {}): React.JSX.Element {
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? "";
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [sort, setSort] = useState<ThreadSort>("activity");
  const [offset, setOffset] = useState(0);
  const [composerOpen, setComposerOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        setState({ kind: "loading" });
        const data = await fetchThreadsFn(slug, {
          sort,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled) return;
        setState({ kind: "ready", data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated(`/community/c/${slug}`);
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setState({ kind: "not_member" });
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "category_unknown" });
          return;
        }
        const msg =
          err instanceof Error ? err.message : "Could not load the threads.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchThreadsFn, slug, sort, offset, onUnauthenticated]);

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const thread = await createThreadFn(slug, {
        title: newTitle,
        body_md: newBody,
      });
      void navigate(`/community/t/${thread.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.body) {
        const body = err.body as { detail?: { blocking?: string; message?: string } };
        setSubmitError(
          body.detail?.message ??
            body.detail?.blocking ??
            err.message,
        );
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Could not post the thread.");
      }
      setSubmitting(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading {slug}…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="mt-4 text-muted-foreground">
          The community is open to active learners and certified
          practitioners.
        </p>
        <Link
          to="/community"
          className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back to community
        </Link>
      </section>
    );
  }

  if (state.kind === "category_unknown") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          Category not found
        </h1>
        <p className="mt-4 text-muted-foreground">
          The category “{slug}” doesn't exist or has been archived.
        </p>
        <Link
          to="/community"
          className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back to community
        </Link>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="mt-4 text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { category, threads, total, next_offset } = state.data;

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community" className="hover:text-foreground">
          Community
        </Link>{" "}
        <span aria-hidden="true">›</span> {category.title}
      </nav>

      <header className="mt-3">
        <h1 className="text-3xl font-bold text-foreground">
          {category.title}
        </h1>
        {category.description && (
          <p className="mt-2 text-sm text-muted-foreground">
            {category.description}
          </p>
        )}
        {category.requires_certification && (
          <p className="mt-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
            Posting requires certification
          </p>
        )}
      </header>

      {/* Sort + new-thread controls */}
      <div className="mt-8 flex items-center justify-between gap-4">
        <div role="group" aria-label="Sort threads" className="flex gap-2">
          {(["activity", "new", "top"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => {
                setSort(option);
                setOffset(0);
              }}
              className={`rounded-md border px-3 py-1 text-xs font-medium ${
                sort === option
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {option === "activity"
                ? "Recent activity"
                : option === "new"
                  ? "Newest"
                  : "Top"}
            </button>
          ))}
        </div>
        {!category.is_announcement && (
          <button
            type="button"
            onClick={() => {
              setComposerOpen((open) => !open);
            }}
            className="rounded-md border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary hover:text-primary-foreground"
          >
            {composerOpen ? "Cancel" : "New thread"}
          </button>
        )}
      </div>

      {composerOpen && (
        <form
          className="mt-6 space-y-4 rounded-lg border border-border bg-muted/30 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <div>
            <label
              htmlFor="new-thread-title"
              className="block text-sm font-medium text-foreground"
            >
              Title
            </label>
            <input
              id="new-thread-title"
              type="text"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
              }}
              minLength={4}
              maxLength={200}
              required
              disabled={submitting}
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
            />
          </div>
          <Composer
            label="Body"
            value={newBody}
            onChange={setNewBody}
            minLength={10}
            maxLength={20_000}
            disabled={submitting}
            placeholder="Markdown is supported. Be respectful and on-topic."
          />
          {submitError && (
            <p className="text-sm text-red-700" role="alert">
              {submitError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setComposerOpen(false);
                setNewTitle("");
                setNewBody("");
                setSubmitError(null);
              }}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || newTitle.length < 4 || newBody.length < 10}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "Posting…" : "Post thread"}
            </button>
          </div>
        </form>
      )}

      {/* Thread list */}
      {threads.length === 0 ? (
        <p className="mt-12 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No threads here yet. Start one!
        </p>
      ) : (
        <ol className="mt-8 space-y-3">
          {threads.map((thread) => (
            <li key={thread.id}>
              <ThreadCard thread={thread} categorySlug={slug} />
            </li>
          ))}
        </ol>
      )}

      {/* Pagination */}
      {(offset > 0 || next_offset !== null) && (
        <nav className="mt-8 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => {
              setOffset((o) => Math.max(0, o - PAGE_SIZE));
            }}
            disabled={offset === 0}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            ← Previous
          </button>
          <p className="text-xs text-muted-foreground">
            {offset + 1}–{Math.min(offset + threads.length, total)} of {total}
          </p>
          <button
            type="button"
            onClick={() => {
              if (next_offset !== null) setOffset(next_offset);
            }}
            disabled={next_offset === null}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            Next →
          </button>
        </nav>
      )}
    </section>
  );
}

function ThreadCard({
  thread,
  categorySlug,
}: {
  thread: ThreadListItem;
  categorySlug: string;
}): React.JSX.Element {
  return (
    <Link
      to={`/community/t/${thread.id}`}
      state={{ categorySlug }}
      className="block rounded-lg border border-border bg-white p-4 hover:border-primary hover:shadow-sm transition-all"
    >
      <div className="flex items-baseline gap-2">
        {thread.is_pinned && (
          <span aria-label="Pinned" title="Pinned">📌</span>
        )}
        {thread.is_locked && (
          <span aria-label="Locked" title="Locked">🔒</span>
        )}
        <h2 className="text-base font-semibold text-foreground">
          {thread.title}
        </h2>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <p>
          by {thread.author.display_name}
          {thread.author.is_certified && " · ✓ Certified"}
        </p>
        <p>
          {thread.reply_count} {thread.reply_count === 1 ? "reply" : "replies"}{" "}
          · last activity {formatActivityTime(thread.last_activity_at)}
        </p>
      </div>
    </Link>
  );
}
